import { useCallback, useEffect, useState } from "react";
import { Form, Select } from "semantic-ui-react";
import { UserData, UserRole } from "../../actions/authentificationActions";
import { AppState } from "../../store/configureStore";
import { useSelector } from "react-redux";
import { Breadcrumbs } from "../../components/Breadcrumbs/Breadcrumbs";
import { Redirect } from "react-router-dom";
import {
    displayNetworkCodeOption,
    IParameter,
    NetworkCode,
    networkCodeOptions,
} from "../../models/parameter";
import "./ImplementationMaps.css";
import { getParameters } from "../../services/parametersService";
import { toast } from "../..";
import MapChart from "../DataDelivery/MapChart";
import { IParameterAnswer } from "../../models/parameterAnswer";
import { getParameterAnswers } from "../../services/parameterAnswerService";
import { getNonInvolvedCountries } from "../../services/countriesService";
import { EVisibility } from "../../models/form";
import { sortBySummaryThenByName } from "../../helpers/commonHelper";

export const ImplementationMaps = () => {
    const [networkCode, setNetworkCode] = useState<NetworkCode | undefined>(undefined);
    const [parameters, setParameters] = useState<IParameter[]>([]);
    const [selectedParameter, setSelectedParameter] = useState<IParameter | undefined>(undefined);
    const [loadingParameters, setLoadingParameters] = useState(false);
    const [loadingParameterAnswers, setLoadingParameterAnswers] = useState(false);
    const [loadingCountries, setLoadingCountries] = useState(false);
    const [parameterAnswers, setParameterAnswers] = useState<IParameterAnswer[]>([]);
    const [nonInvolvedCountries, setNonInvolvedCountries] = useState<string[]>([]);

    const user: UserData = useSelector((state: AppState) => state.user);

    const fetchParameters = async (networkCode: number) => {
        if (networkCode === undefined) {
            setParameters([]);
            return;
        }
        try {
            setLoadingParameters(true);
            const parameterFilter = {
                name: "",
                networkCode: networkCode,
                visibility: user.role === undefined ? EVisibility.Public : undefined,
                year: "",
                hidden: user.role === UserRole.NCLP ? true : undefined,
            };
            const res = await getParameters(parameterFilter, 1, 1000, false, true);
            setParameters(sortBySummaryThenByName(res.result));
        } catch {
            toast("Error fetching parameters", false, 1000);
        }
        setLoadingParameters(false);
    };

    const fetchParameterAnswers = async (selectedParameterGuid: string) => {
        try {
            setLoadingParameterAnswers(true);
            const res = await getParameterAnswers(selectedParameterGuid);
            setParameterAnswers(res);
        } catch {
            toast("Error fetching parameter answers", false, 1000);
        }
        setLoadingParameterAnswers(false);
    };

    const fetchNonInvolvedCountries = useCallback(async () => {
        try {
            setLoadingCountries(true);
            const res = await getNonInvolvedCountries();
            setNonInvolvedCountries(res);
        } catch {
            toast("Error fetching non-involved countries", false, 1000);
        }
        setLoadingCountries(false);
    }, []);

    useEffect(() => {
        fetchNonInvolvedCountries();
    }, [fetchNonInvolvedCountries]);

    if (user.role === UserRole.Viewer) {
        return <Redirect to="/unauthorized" />;
    }

    return (
        <>
            <div className="input-data-editor-container">
                <div className="container-header">
                    <h1>Implementation maps</h1>
                </div>
                <Breadcrumbs />
                <div className="implementation-maps-container">
                    <div className="maps-selection-container">
                        <Form>
                            <Form.Field>
                                <label>Network code</label>
                                <Select
                                    clearable
                                    selectOnBlur={false}
                                    className="filter-selection"
                                    options={networkCodeOptions}
                                    value={networkCode}
                                    onChange={(_e, data) => {
                                        setNetworkCode(
                                            data.value === ""
                                                ? undefined
                                                : (data.value as NetworkCode)
                                        );
                                        setSelectedParameter(undefined);

                                        if (data.value === "") {
                                            setParameters([]);
                                            setParameterAnswers([]);
                                        } else {
                                            fetchParameters(data.value as number);
                                        }
                                    }}
                                    text={
                                        networkCode !== undefined
                                            ? displayNetworkCodeOption(networkCode)
                                            : "Select network code"
                                    }
                                    disabled={loadingCountries}
                                />
                            </Form.Field>
                            <Form.Field>
                                <label>Parameter</label>
                                <Select
                                    clearable
                                    selectOnBlur={false}
                                    className="filter-selection"
                                    options={parameters
                                        .filter(x =>
                                            !user.role ? x.visibility === EVisibility.Public : x
                                        )
                                        .map(x => ({
                                            key: x.guid,
                                            text: x.name,
                                            value: x.guid,
                                        }))}
                                    value={selectedParameter?.guid}
                                    onChange={(_e, data) => {
                                        setSelectedParameter(
                                            data.value === ""
                                                ? undefined
                                                : parameters.find(x => x.guid === data.value)
                                        );
                                        if (data.value === "") {
                                            setParameterAnswers([]);
                                        } else {
                                            fetchParameterAnswers(data.value as string);
                                        }
                                    }}
                                    text={selectedParameter?.name ?? "Select parameter"}
                                    disabled={
                                        networkCode === undefined ||
                                        loadingParameters ||
                                        loadingParameterAnswers
                                    }
                                />
                            </Form.Field>
                            <Form.Field>
                                <label>Legend</label>
                                <table className="legend-table">
                                    <tbody>
                                        <tr>
                                            <td className="legend-cell answer7">
                                                {selectedParameter?.answer7text ??
                                                    "NI-E - Submitted for approval"}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="legend-cell answer6">
                                                {selectedParameter?.answer6text ??
                                                    "NI-D - Preliminary, shared with stakeholders and publicly available"}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="legend-cell answer5">
                                                {selectedParameter?.answer5text ??
                                                    "NI-C - Preliminary, shared with stakeholders, but not publicly available"}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="legend-cell answer4">
                                                {selectedParameter?.answer4text ??
                                                    "NI-B - TSO internal consideration"}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="legend-cell answer3">
                                                {selectedParameter?.answer3text ??
                                                    "NI-A - No consideration"}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="legend-cell answer2">
                                                {selectedParameter?.answer2text ??
                                                    "NI-F - Not applicable"}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="legend-cell answer1">
                                                {selectedParameter?.answer1text ??
                                                    "0 - Needs no implementation"}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="legend-cell non-eu">
                                                Country not in scope
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </Form.Field>
                            {selectedParameter !== undefined &&
                                parameterAnswers.some(x => x.remark !== "") && (
                                    <Form.Field>
                                        <label>Countries with remarks:</label>
                                        <div>
                                            {parameterAnswers
                                                .filter(x => x.remark !== "")
                                                .map(x => (
                                                    <div key={x.countryGuid}>{x.countryName}</div>
                                                ))}
                                        </div>
                                    </Form.Field>
                                )}
                        </Form>
                    </div>
                    <div className="maps-container">
                        <MapChart
                            isPercentage={false}
                            parameterAnswers={parameterAnswers}
                            parameters={parameters}
                            parameterGuid={selectedParameter?.guid ?? ""}
                            nonInvolvedCountries={nonInvolvedCountries}
                            networkCode={networkCode}
                            refreshTrigger={() =>
                                fetchParameterAnswers(selectedParameter?.guid ?? "")
                            }
                        />
                    </div>
                </div>
            </div>
        </>
    );
};
